import Axios from "axios";
import AxiosConfig from "src/utilities/AxiosConfig";
import { toVault77ApiError } from "../../core/apiError";
import { EMPLOYEE_REVIEWS_ENDPOINT } from "./constants";
import type {
    EmployeeReview,
    CreateEmployeeReviewRequest,
    UpdateEmployeeReviewRequest,
} from "../types";

/**
 * Fetch all employee reviews. Optionally filter by employeeId.
 * GET api/vault77/employeeReviews
 */
export async function getEmployeeReviews(employeeId?: number): Promise<EmployeeReview[]> {
    try {
        const url = employeeId != null
            ? `${EMPLOYEE_REVIEWS_ENDPOINT}?employeeId=${employeeId}`
            : EMPLOYEE_REVIEWS_ENDPOINT;
        const response = await Axios.get<EmployeeReview[]>(url, AxiosConfig.configValue("min"));
        return response.data;
    } catch (error) {
        console.error("getEmployeeReviews/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch employee reviews.");
    }
}

/**
 * Fetch a single employee review by ID.
 * GET api/vault77/employeeReviews/{id}
 */
export async function getEmployeeReview(id: number): Promise<EmployeeReview> {
    try {
        const response = await Axios.get<EmployeeReview>(
            `${EMPLOYEE_REVIEWS_ENDPOINT}/${id}`,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("getEmployeeReview/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to fetch employee review.");
    }
}

/**
 * Create a new employee review.
 * POST api/vault77/employeeReviews
 */
export async function createEmployeeReview(
    request: CreateEmployeeReviewRequest
): Promise<EmployeeReview> {
    try {
        const response = await Axios.post<EmployeeReview>(
            EMPLOYEE_REVIEWS_ENDPOINT,
            request,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("createEmployeeReview/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to create employee review.");
    }
}

/**
 * Update an existing employee review.
 * PUT api/vault77/employeeReviews  (ID is in the request body, not the URL)
 */
export async function updateEmployeeReview(
    request: UpdateEmployeeReviewRequest
): Promise<EmployeeReview> {
    try {
        const response = await Axios.put<EmployeeReview>(
            EMPLOYEE_REVIEWS_ENDPOINT,
            request,
            AxiosConfig.configValue("min")
        );
        return response.data;
    } catch (error) {
        console.error("updateEmployeeReview/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to update employee review.");
    }
}

/**
 * Delete an employee review by ID (soft delete).
 * DELETE api/vault77/employeeReviews/{id}
 */
export async function deleteEmployeeReview(id: number): Promise<void> {
    try {
        await Axios.delete(
            `${EMPLOYEE_REVIEWS_ENDPOINT}/${id}`,
            AxiosConfig.configValue("min")
        );
    } catch (error) {
        console.error("deleteEmployeeReview/api ~ error:", error);
        throw toVault77ApiError(error, "Unable to delete employee review.");
    }
}
